import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, preventAccidentalDismiss } from "@/components/ui/Modal";
import React, { useEffect, useRef, useState } from "react";
import Step1 from "./Step1";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { StepIndicator } from "@/components/ui/StepIndicator";
import Step2 from "./Step2";
import Step3 from "./Step3";
import { JobService } from "@/services/jobService";
import RegenLoader from "@/components/shared/RegenLoader";
import toast from "react-hot-toast";

const INITIAL_AI_DATA = {
  file: null,
  description: "",
  improvementType: "",
};

const AIModal = ({ open, onClose, onBack, aiStep, setAiStep, onSuccess, userOptions }) => {
  const AI_STEP_CONFIG = [
    { key: "upload", label: "Upload JD", component: Step1, isValid: () => isStep1Valid, errorMessage: "Please upload JD or enter description" },
    { key: "customize", label: "Customize", component: Step2, isValid: () => isStep2Valid, errorMessage: "Please select improvement type and response depth" },
    { key: "preview", label: "Review & Update", component: Step3 },
  ];

  const step3SubmitRef = useRef(null);
  const step3NextRef = useRef(null);
  const cancelledRef = useRef(false);
  const step1ScrollRef = useRef(null);
  const step2ScrollRef = useRef(null);
  const scrollableRef = useRef(null);

  const [data, setData] = useState(INITIAL_AI_DATA);
  const [isStreaming, setIsStreaming] = useState(false);
  const [progress, setProgress] = useState(null);
  const [jobFormData, setJobFormData] = useState({});
  const [jobFormSkills, setJobFormSkills] = useState([]);
  const [step3Tab, setStep3Tab] = useState(1);
  const [isStep3Step1Complete, setIsStep3Step1Complete] = useState(false);
  // Step 3's form owns the buttons' disable here, so its step-2 consent
  // section reports readiness up rather than gating a button of its own.
  const [isStep3ConsentReady, setIsStep3ConsentReady] = useState(false);
  const [lastImprovedConfig, setLastImprovedConfig] = useState(null);

  const isProcessing = isStreaming || data?.isUploading;

  const [step2Methods, setStep2Methods] = useState(null);
  const [stepErrors, setStepErrors] = useState([false, false, false]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleExitAIFlow = () => {
    resetAIState();
    onClose();
  };

  const resetAIState = () => {
    cancelledRef.current = true;
    setAiStep(1);
    setStep3Tab(1);
    setData(INITIAL_AI_DATA);
    setIsStreaming(false);
    setProgress(null);
    setJobFormData({});
    setJobFormSkills([]);
    setIsStep3Step1Complete(false);
    setIsStep3ConsentReady(false);
    setLastImprovedConfig(null);
    setStepErrors([false, false, false]);
  };

  const handleImproveWithAI = async () => {
    const jdMode = data.jdMode || "improve";

    if (jdMode !== "extract") {
      const valid = await step2Methods?.trigger();

      if (!valid) {
        setStepErrors((prev) => [prev[0], true, prev[2]]);
        scrollToStep2Error();
        return;
      }
    }

    setLastImprovedConfig(getStep2Config());
    cancelledRef.current = false;
    setIsStreaming(true);
    setProgress({ percentage: 0, message: jdMode === "extract" ? "AI is extracting your JD..." : "AI is improving your JD..." });

    let receivedResult = false;
    let advanced = false;
    let streamError = false;

    const jdFiles = data.file ? [{ filename: data.file.name, content: data.extractedText }] : [{ filename: "jd.txt", content: data.description }];

    const payload =
      jdMode === "extract"
        ? {
          improvement_type: "extraction_only",
          response_depth: "Standard Response",
          jd_files: jdFiles,
        }
        : {
          improvement_type: data.improvementType,
          response_depth: data.responseDepth,
          enhancement_tone: data.enhancement_tone,
          improvement_flexibility: data.improvement_flexibility,
          jd_files: jdFiles,
          custom_focus: data.improvementType === "custom" ? data.customFocus : "",
        };

    try {
      await JobService.jdImproverStream(payload, (parsed) => {
        if (cancelledRef.current) return;
        if (parsed.type === "progress") {
          setProgress({ percentage: parsed.percentage, message: parsed.message });
        }
        if (parsed.type === "error") {
          streamError = parsed.error || "AI improvement failed. Please try again.";
        }
        if (parsed.type === "file_complete") {
          receivedResult = true;
          setJobFormData({
            title: parsed.title || "",
            role_overview: parsed.role_overview || "",
            key_responsibilities: parsed.key_responsibilities || "",
            location: parsed.location || "",
            employeeType: parsed.employee_type || "",
            shiftTiming: parsed.shift_timing || "",
            nice_to_have: parsed.nice_to_have || "",
            department: parsed.department || "",
            division: parsed.division || "",
            company_website: parsed.company_website || "",
            company_linkedin: parsed.company_linkedin || "",
            certifications: parsed.certifications || [],
            languages_required: parsed.languages_required || [],
            interviewers: parsed.interviewer_details || [],
            deadline: parsed?.application_deadline?.value || "",
            vacancy_count: parsed?.vacancy_count || "",
            hiring_priority: parsed?.hiring_priority || "",
            experience_min: parsed?.experience_min ?? "",
            experience_max: parsed?.experience_max ?? "",
          });
          if (parsed.skills) setJobFormSkills(parsed.skills);
        }
        if (parsed.type === "complete") {
          advanced = true;
          setAiStep(3);
        }
      });
    } catch (err) {
      console.error(err);
    } finally {
      if (cancelledRef.current) return;
      setIsStreaming(false);

      if (streamError) {
        toast.error(streamError, { position: "top-right" });
      } else if (receivedResult && !advanced) {
        setAiStep(3);
      } else if (!receivedResult) {
        toast.error("AI improvement failed. Please try again.", { position: "top-right" });
      }
    }
  };

  const isExtractMode = (data?.jdMode || "improve") === "extract";

  const isStep1Valid = (!!data?.file || !!data?.description?.trim()) && !data?.uploadError;
  // const isStep2Valid =
  //   !!data?.improvementType &&
  //   !!data?.responseDepth &&

  //   (data.improvementType !== "custom" || !!data?.customFocus?.trim()) &&
  //   data?.enhancement_tone &&
  //   data?.improvement_flexibility;

  const isStep2Valid =
    (data?.jdMode || "improve") === "extract"
      ? true
      : Boolean(
        data?.improvementType &&
        data?.responseDepth &&
        (data.improvementType !== "custom" || data?.customFocus?.trim()) &&
        data?.enhancement_tone &&
        data?.improvement_flexibility
      );

  useEffect(() => {
    if (isStep1Valid) {
      setStepErrors((prev) => [false, prev[1], prev[2]]);
    }
  }, [isStep1Valid]);

  useEffect(() => {
    if (isStep2Valid) {
      setStepErrors((prev) => [prev[0], false, prev[2]]);
    }
  }, [isStep2Valid]);

  const getStep2Config = () => ({
    jdMode: data.jdMode || "improve",
    improvementType: data.improvementType || "",
    responseDepth: data.responseDepth || "",
    enhancement_tone: data.enhancement_tone || "",
    improvement_flexibility: data.improvement_flexibility || "",
    customFocus: data.customFocus || "",
  });

  const step2HasChanges = React.useMemo(() => {
    if (!lastImprovedConfig) return true;
    const current = getStep2Config();
    return JSON.stringify(current) !== JSON.stringify(lastImprovedConfig);
  }, [data.jdMode, data.improvementType, data.responseDepth, data.enhancement_tone, data.improvement_flexibility, data.customFocus, lastImprovedConfig]);

  const handlePostJob = async (formData) => {
    try {
      setIsSubmitting(true);
      await JobService.manualJobPost(formData, formData.skills || []);
      toast.success("Job posted successfully.", { position: "top-right" });
      onSuccess?.();
      handleExitAIFlow();
    } catch (err) {
      console.error("Error posting job:", err);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPostJob = aiStep === 3 && step3Tab === 2;
  const hasAIResult = Object.keys(jobFormData).length > 0;

  const showForwardButton = aiStep === 2 && hasAIResult && !isProcessing && !step2HasChanges;

  const canImprove = aiStep !== 2 || (!isStreaming && isStep2Valid && (!hasAIResult || step2HasChanges));

  const isActionDisabled =
    isStreaming ||
    isProcessing ||
    isSubmitting ||
    (aiStep === 2 && !canImprove) ||
    // `isPostJob` is step 3's field-config step. Its consent switches are
    // pre-set from the org default after their own fetch, and Post Job before
    // that lands would create a job whose consent columns were never written.
    (isPostJob && !isStep3ConsentReady);
  aiStep === 1 && data?.uploadError;

  // ── Scroll to first invalid field
  const scrollToError = (targetRef) => {
    if (!targetRef?.current || !scrollableRef?.current) return;
    const container = scrollableRef.current;
    const target = targetRef.current;
    const zoom = 0.85;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset = (targetRect.top - containerRect.top) / zoom + container.scrollTop - 80;
    container.scrollTo({ top: offset, behavior: "smooth" });
  };

  const scrollToStep1Error = () => scrollToError(step1ScrollRef);
  const scrollToStep2Error = () => scrollToError(step2ScrollRef);

  const stepsWithErrors = stepErrors;

  const stepsReady = [isStep1Valid, isStep2Valid, Object.keys(jobFormData).length > 0];

  // ── Extract mode skips the Customize step entirely, so the indicator only
  // shows "Upload JD" and "Preview" and aiStep jumps 1 -> 3 directly.
  const visibleStepConfig = isExtractMode
    ? AI_STEP_CONFIG.filter((step) => step.key !== "customize")
    : AI_STEP_CONFIG;

  const visibleIndexForStep = (step) => {
    const key = AI_STEP_CONFIG[step - 1].key;
    return visibleStepConfig.findIndex((s) => s.key === key);
  };

  const visibleCurrentStep = visibleIndexForStep(aiStep) + 1;

  const filterVisible = (arr) =>
    isExtractMode ? AI_STEP_CONFIG.map((step, i) => arr[i]).filter((_, i) => AI_STEP_CONFIG[i].key !== "customize") : arr;

  const visibleStepsWithErrors = filterVisible(stepsWithErrors);
  const visibleStepsReady = filterVisible(stepsReady);

  return (
    <Dialog
      open={open}
      onOpenChange={preventAccidentalDismiss(() => {
        resetAIState();
        onClose();
      })}
    >
      <DialogContent className="max-h-[88vh] flex flex-col w-[320px] md:w-full">
        {/* Header */}
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0" style={{ zoom: 0.95 }}>
          <div className="pr-20">
            <DialogTitle className="text-[15px] font-semibold text-text-primary">AI Powered Job Description</DialogTitle>
            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">AI will polish your JD based on your answers</DialogDescription>
          </div>
          <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <Button
              disabled={isProcessing}
              onClick={() => {
                if (aiStep === 1) {
                  resetAIState();
                  onBack();
                } else if (aiStep === 3 && step3Tab === 2) {
                  setStep3Tab(1);
                } else if (aiStep === 3 && isExtractMode) {
                  setAiStep(1);
                } else {
                  setAiStep(aiStep - 1);
                }
              }}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-button-tertiary border border-border-primary text-text-primary text-[13px]"
            >
              <ChevronLeft size={14} /> <span className="hidden md:inline">Back</span>
            </Button>
            <DialogClose onClick={isProcessing ? undefined : handleExitAIFlow}>
              <ButtonShadcn
                variant="ghost"
                size="icon-sm"
                aria-label="Close"
                className="rounded-full bg-background-icon/40 hover:bg-background-icon/60"
              >
                <X className="w-3.5 h-3.5" strokeWidth={3} />
              </ButtonShadcn>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div ref={scrollableRef} className="flex-1 overflow-y-auto custom-scrollbar relative" style={{ zoom: 0.95 }}>
          {/* Step indicator */}
          <div className="px-5 pt-3 pb-1">
            <StepIndicator
              stepsConfig={visibleStepConfig}
              currentStep={visibleCurrentStep}
              stepClick={(i) => {
                if (isProcessing) return;
                const targetKey = visibleStepConfig[i].key;
                const targetStepIndex = AI_STEP_CONFIG.findIndex((step) => step.key === targetKey);
                for (let s = 0; s < targetStepIndex; s++) {
                  const stepConfig = AI_STEP_CONFIG[s];
                  if (isExtractMode && stepConfig.key === "customize") continue;
                  if (stepConfig.isValid && !stepConfig.isValid()) {
                    toast.error(stepConfig.errorMessage);
                    return;
                  }
                }
                setAiStep(targetStepIndex + 1);
              }}
              stepsWithErrors={visibleStepsWithErrors}
              stepsReady={visibleStepsReady}
            />
          </div>

          {/* Step content */}
          <div className="px-5 pb-2">
            {(() => {
              const current = AI_STEP_CONFIG[aiStep - 1];
              const StepComponent = current.component;
              if (isStreaming) {
                return <RegenLoader progress={progress} messageType="jd" />;
              }
              const stepProps = {
                upload: { data, setData, isProcessing, scrollRef: step1ScrollRef },
                customize: { data, setData, scrollRef: step2ScrollRef, setFormMethods: setStep2Methods },
                preview: {
                  data: jobFormData,
                  jobFormSkills,
                  onClose: handleExitAIFlow,
                  onSubmit: handlePostJob,
                  submitRef: step3SubmitRef,
                  nextRef: step3NextRef,
                  onStep1CompleteChange: setIsStep3Step1Complete,
                  onConsentReadyChange: setIsStep3ConsentReady,
                  jobId: null,
                  activeTab: step3Tab,
                  setActiveTab: setStep3Tab,
                  isSubmitting,
                  userOptions
                },
              }[current.key];
              return <StepComponent {...stepProps} />;
            })()}
          </div>

          {/* Footer inside scroll area */}
          <DialogFooter className="border-none px-5 pb-4 pt-1">
            {showForwardButton && (
              <Button
                disabled={isProcessing}
                onClick={() => setAiStep(3)}
                className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-button-tertiary border border-border-primary text-text-primary text-[13px]"
              >
                <span className="hidden md:inline">Forward</span> <ChevronRight size={14} />
              </Button>
            )}

            {!isStreaming && !(aiStep === 2 && showForwardButton) && (
              <Button
                onClick={() => {
                  if (aiStep === 1) {
                    if (!isStep1Valid) {
                      setStepErrors((prev) => [true, prev[1], prev[2]]);
                      scrollToStep1Error();
                      return;
                    }
                    setData((prev) => ({ ...prev, finalText: prev.extractedText || prev.description || "" }));
                    if (isExtractMode) {
                      handleImproveWithAI();
                    } else {
                      setAiStep(2);
                    }
                  } else if (aiStep === 2) {
                    // if (!isStep2Valid) {
                    //   scrollToStep2Error();
                    //   return;
                    // }
                    handleImproveWithAI();
                  } else if (aiStep === 3) {
                    if (step3Tab === 1) {
                      step3NextRef.current?.();
                    } else {
                      step3SubmitRef.current?.();
                    }
                  }
                }}
                disabled={isActionDisabled}
                className={`bg-button-secondary text-white text-[13px] font-medium flex flex-1 justify-center md:flex-none gap-1.5 items-center disabled:opacity-50 disabled:cursor-not-allowed
                ${isActionDisabled || isStreaming ? "opacity-50 cursor-not-allowed" : "hover:bg-button-secondary/80"}
              `}
              >
                {aiStep === 1
                  ? (isExtractMode ? "Extract with AI" : "Next")
                  : aiStep === 2
                    ? ((data.jdMode || "improve") === "extract" ? "Extract with AI" : "Improve with AI")
                    : isPostJob
                      ? (isSubmitting ? "Posting..." : "Post Job")
                      : "Next"}
                {!((aiStep === 1 && isExtractMode) || aiStep === 2) && !isPostJob && <ChevronRight size={14} />}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIModal;
